import { useEffect, useState } from 'react';
import { getListPrice } from 'src/common/hooks/widgets/fetchData';
import {
  EDR_DISCOUNT,
  EDR_DISCOUNT_SW,
  EDR_SAAS_PN,
  EDR_SW_PN,
  EDR_SW_RATIO,
  EDR_TIER_1,
  EDR_TIER_INF,
  YEARLY,
} from '../constants/price';
import { useCurrencyWithSymbol } from 'src/common/hooks/widgets/useCurrencyWithSymbol';

/**
 * Calculate the estimated price
 *
 * @param {string} langCode: to define the language
 * @param {string} localCurrency the language code to get the currency sign for
 * @param {number} endpoints : Number of endpointss
 * @param {boolean} mdr : Includes MDR
 * @param {boolean} onEDRSoftware : for edr enterprise
 * @returns {Array}
 */

export interface PropsFunc {
  currency: string;
  localeCode: string;
  country: string;
  endpoints: number;
  onEDRSoftware: boolean;
}

export interface IFetchError {
  status: number;
  statusText: string;
}

export interface priceProps {
  partNumber: string;
  pricingIndicator: string;
  pricingModel: number;
  scaleQtyPrice: [
    {
      qty: number;
      price: number;
    },
  ];
}

const partsToQuery = [
  {
    partNumber: EDR_SAAS_PN,
  },
  {
    partNumber: EDR_SW_PN,
  },
];

type MarketPlaceRequest = {
  currency: string;
  countryCode3: string;
  parts: typeof partsToQuery;
};

interface PriceParts {
  partNumber: string;
  pricingIndicator: string;
  pricingModel: number;
  scaleQtyPrice: ScaleQtyPrice[];
}

interface ScaleQtyPrice {
  qty: number;
  price: number;
}

interface Price {
  price: number;
}

export const usePrice = ({
  currency,
  localeCode,
  country,
  endpoints,
  onEDRSoftware,
}: PropsFunc) => {
  const [totalYrlyCost, setTotalYrlyCost] = useCurrencyWithSymbol(
    currency,
    localeCode,
    0,
  );
  const [totalMthlyCost, setTotalMthlyCost] = useCurrencyWithSymbol(
    currency,
    localeCode,
    0,
  );
  const [totalCost, setTotalCost] = useState(0);
  const [edrSaasTier, setEDRSaasTier] = useState<Price[]>([]);
  const [edrSoftwareTier, setEDRSoftwareTier] = useState<Price[]>([]);
  const [dataFetched, setDataFetched] = useState([]);
  const [fetchingError, setFetchingError] = useState({});

  const isUndefinedForNumber = (value: number | undefined) => {
    return value ? value : 0;
  };

  const calculatePricing = (qty: number, price: number) => {
    return qty * price;
  };

  const calculatedEDRSaasPrice = () => {
    return calculatePricing(
      endpoints,
      edrSaasTier[0] ? edrSaasTier[0].price : 0,
    );
  };

  const calculatedEDRSoftwarePrice = () => {
    const ratio = endpoints * EDR_SW_RATIO;
    const price = ratio * edrSoftwareTier[0]?.price;
    return price * (1 - EDR_DISCOUNT_SW);
  };

  const setPricesData = (products: priceProps[]) => {
    if (products?.length) {
      // Saas
      const EDR_SAAS = products.find(
        (i: PriceParts) => i.partNumber === EDR_SAAS_PN,
      );
      const EDR_SAAS_PRICE_ONE = EDR_SAAS?.scaleQtyPrice.find(
        (i: ScaleQtyPrice) => i.qty === EDR_TIER_1,
      )?.price;
      const EDR_SAAS_PRICE_TWO = EDR_SAAS?.scaleQtyPrice.find(
        (i: ScaleQtyPrice) => i.qty === EDR_TIER_INF,
      )?.price;

      const EDR_SAAS_PRICE: number[] = [
        isUndefinedForNumber(EDR_SAAS_PRICE_ONE),
        isUndefinedForNumber(EDR_SAAS_PRICE_TWO),
      ];
      setEDRSaasTier(
        EDR_SAAS_PRICE.map((price: number) => ({
          price: price ? price * (1 - EDR_DISCOUNT) : 0,
        })),
      );

      // Software
      const EDR_SOFTWARE_PN = products.find(
        (i: PriceParts) => i.partNumber === EDR_SW_PN,
      );
      const EDR_SOFTWARE_PRICE_ONE = EDR_SOFTWARE_PN?.scaleQtyPrice.find(
        (i: ScaleQtyPrice) => i.qty === 1,
      )?.price;

      const EDR_ENTERPRISE_PRICE: number[] = [
        isUndefinedForNumber(EDR_SOFTWARE_PRICE_ONE),
      ];
      setEDRSoftwareTier(
        EDR_ENTERPRISE_PRICE.map((price: number) => ({
          price: price ? price / 100 : 0,
        })),
      );
    }
  };

  const fetchPrices = () => {
    const body: MarketPlaceRequest = {
      currency: currency,
      countryCode3: country,
      parts: partsToQuery,
    };
    getListPrice(
      `https://api.marketplace.ibm.com/purchase/catalog/resources/price/info`,
      JSON.stringify(body),
      'POST',
    )
      .then((data) => {
        if (data.parts && data.parts.length !== 0) {
          setPricesData(data.parts);
          setDataFetched(data.parts);
        } else {
          setDataFetched([]);
        }
      })
      .catch((error) => {
        setDataFetched([]);
        if (error.response) {
          setFetchingError(error.response);
        }
      });
  };

  const price =
    endpoints >= 100
      ? onEDRSoftware
        ? calculatedEDRSoftwarePrice()
        : calculatedEDRSaasPrice()
      : 0;

  useEffect(() => {
    if (currency && country && dataFetched.length === 0) {
      fetchPrices();
    }
  }, [localeCode, currency, country]);

  useEffect(() => {
    setTotalYrlyCost(Math.round(price * YEARLY));
    setTotalMthlyCost(Math.round(price));
    setTotalCost(Math.round(price));
  }, [endpoints, edrSoftwareTier, edrSaasTier, onEDRSoftware, price]);

  return [totalCost, totalYrlyCost, totalMthlyCost, fetchingError];
};
